import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(() => ({
  note: {
    background: "rgba(37,211,102,0.10)",
    border: "1px solid rgba(37,211,102,0.35)",
    borderRadius: 8,
    padding: 12,
    fontSize: 13,
    marginBottom: 16,
  },
  warn: {
    background: "rgba(255,167,38,0.12)",
    border: "1px solid rgba(255,167,38,0.5)",
    borderRadius: 8,
    padding: 12,
    fontSize: 13,
    marginBottom: 16,
  },
  row: { display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 16 },
  stepsBox: { marginTop: 12 },
  step: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    padding: "8px 0",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  stepTitle: { fontWeight: 600, fontSize: 13 },
  stepDesc: { fontSize: 12, color: "#666", marginTop: 2 },
}));

const MODELS = ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1"];

const PlaybookTab = () => {
  const classes = useStyles();
  const fileRef = useRef(null);
  const [pb, setPb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/company-playbook");
      setPb(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = async (partial) => {
    setSaving(true);
    try {
      const { data } = await api.put("/company-playbook", partial);
      setPb((prev) => ({ ...prev, ...data }));
    } catch (err) {
      toastError(err);
      load(); // recarrega estado real em caso de erro (ex.: ativar sem passos)
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("typeArch", "aiknowledge");
      const { data } = await api.post(
        "/company-playbook/upload?typeArch=aiknowledge",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPb((prev) => ({ ...prev, ...data }));
      toast.success("Playbook processado e passos extraídos.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) return <CircularProgress size={22} />;

  const steps = Array.isArray(pb?.steps) ? pb.steps : [];

  return (
    <div>
      <div className={classes.note}>
        📘 <b>Playbook de Vendas</b> — vale para <b>toda a empresa</b> (não é por
        agente). A IA lê o arquivo, extrai os passos e, durante os atendimentos
        abertos, sugere ao <b>atendente</b> (num cartão flutuante) o próximo passo
        para converter. Nunca responde o cliente sozinho.
      </div>

      {pb && pb.hasApiKey === false && (
        <div className={classes.warn}>
          ⚠️ Nenhum agente de IA ativo com chave OpenAI foi encontrado. O playbook
          usa a chave de um agente ativo — configure um agente com chave primeiro.
        </div>
      )}

      <div className={classes.row}>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.txt"
          style={{ display: "none" }}
          onChange={onPickFile}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={
            uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />
          }
          disabled={uploading}
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          {pb?.fileName ? "Trocar arquivo" : "Enviar arquivo (PDF/DOCX/TXT)"}
        </Button>
        {pb?.fileName && <Chip label={pb.fileName} />}
      </div>

      <div className={classes.row}>
        <FormControlLabel
          control={
            <Switch
              checked={!!pb?.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              color="primary"
              disabled={saving}
            />
          }
          label={pb?.enabled ? "Ativado" : "Desativado"}
        />

        <FormControl style={{ minWidth: 160 }}>
          <InputLabel>Modelo</InputLabel>
          <Select
            value={pb?.model || "gpt-4o-mini"}
            onChange={(e) => patch({ model: e.target.value })}
          >
            {MODELS.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Intervalo (segundos)"
          type="number"
          value={pb?.intervalSeconds ?? 90}
          inputProps={{ min: 30, max: 3600 }}
          onChange={(e) =>
            setPb((prev) => ({ ...prev, intervalSeconds: e.target.value }))
          }
          onBlur={(e) => patch({ intervalSeconds: e.target.value })}
          style={{ width: 160 }}
        />
      </div>

      <div className={classes.stepsBox}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          Passos extraídos {steps.length ? `(${steps.length})` : ""}
        </div>
        {steps.length === 0 ? (
          <div style={{ fontSize: 13, color: "#888" }}>
            Nenhum passo ainda. Envie um arquivo de playbook para extrair os passos.
          </div>
        ) : (
          steps.map((s) => (
            <div className={classes.step} key={s.id}>
              <CheckCircleIcon
                fontSize="small"
                style={{ color: "#25D366", marginTop: 1 }}
              />
              <div>
                <div className={classes.stepTitle}>
                  {s.order}. {s.title}
                </div>
                {s.description && (
                  <div className={classes.stepDesc}>{s.description}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PlaybookTab;
